export interface Project {
  name: string;
  logo: string;
  category: string;
}

// Function to check if a single logo exists
const checkLogoExists = async (url: string): Promise<boolean> => {
  try {
    const response = await fetch(url);
    return response.ok;
  } catch {
    return false;
  }
};

// Function to get random projects with valid logos
export const getRandomProjects = async (count: number): Promise<Project[]> => {
  const shuffled = [...projects].sort(() => Math.random() - 0.5);
  const validProjects: Project[] = [];
  
  // Keep checking projects until we have enough valid ones
  for (const project of shuffled) {
    if (validProjects.length >= count) break;
    
    const logoExists = await checkLogoExists(project.logo);
    if (logoExists) {
      validProjects.push(project);
    }
  }
  
  return validProjects;
};

export const projects: Project[] = [
  {
    name: "Kubernetes",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/kubernetes/icon/color/kubernetes-icon-color.svg",
    category: "Orchestration & Management"
  },
  {
    name: "Prometheus",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/prometheus/icon/color/prometheus-icon-color.svg",
    category: "Observability & Analysis"
  },
  {
    name: "Helm",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/helm/icon/color/helm-icon-color.svg",
    category: "Package Management"
  },
  {
    name: "Envoy",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/envoy/icon/color/envoy-icon-color.svg",
    category: "Service Proxy"
  },
  {
    name: "CoreDNS",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/coredns/icon/color/coredns-icon-color.svg",
    category: "Service Discovery"
  },
  {
    name: "containerd",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/containerd/icon/color/containerd-icon-color.svg",
    category: "Container Runtime"
  },
  {
    name: "Fluentd",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/fluentd/icon/color/fluentd-icon-color.svg",
    category: "Logging"
  },
  {
    name: "Jaeger",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/jaeger/icon/color/jaeger-icon-color.svg",
    category: "Distributed Tracing"
  },
  {
    name: "Vitess",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/vitess/icon/color/vitess-icon-color.svg",
    category: "Database"
  },
  {
    name: "NATS",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/nats/icon/color/nats-icon-color.svg",
    category: "Messaging"
  },
  {
    name: "Argo",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/argo/icon/color/argo-icon-color.svg",
    category: "GitOps & Workflow"
  },
  {
    name: "Linkerd",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/linkerd/icon/color/linkerd-icon-color.svg",
    category: "Service Mesh"
  },
  {
    name: "etcd",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/etcd/icon/color/etcd-icon-color.svg",
    category: "Distributed Database"
  },
  {
    name: "Harbor",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/harbor/icon/color/harbor-icon-color.svg",
    category: "Container Registry"
  },
  {
    name: "Rook",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/rook/icon/color/rook-icon-color.svg",
    category: "Storage"
  },
  {
    name: "TiKV",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/tikv/icon/color/tikv-icon-color.svg",
    category: "Distributed Storage"
  },
  {
    name: "OpenTelemetry",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/opentelemetry/icon/color/opentelemetry-icon-color.svg",
    category: "Observability"
  },
  {
    name: "Flux",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/flux/icon/color/flux-icon-color.svg",
    category: "GitOps"
  },
  {
    name: "Cilium",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/cilium/icon/color/cilium-icon-color.svg",
    category: "Networking"
  },
  {
    name: "Crossplane",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/crossplane/icon/color/crossplane-icon-color.svg",
    category: "Infrastructure"
  },
  {
    name: "Istio",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/istio/icon/color/istio-icon-color.svg",
    category: "Service Mesh"
  },
  {
    name: "CRI-O",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/cri-o/icon/color/cri-o-icon-color.svg",
    category: "Container Runtime"
  },
  {
    name: "Buildpacks",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/buildpacks/icon/color/buildpacks-icon-color.svg",
    category: "Application Build"
  },
  {
    name: "Falco",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/falco/icon/color/falco-icon-color.svg",
    category: "Security"
  },
  {
    name: "Thanos",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/thanos/icon/color/thanos-icon-color.svg",
    category: "Observability"
  },
  {
    name: "Volcano",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/volcano/icon/color/volcano-icon-color.svg",
    category: "Batch Scheduling"
  },
  {
    name: "Longhorn",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/longhorn/icon/color/longhorn-icon-color.svg",
    category: "Storage"
  },
  {
    name: "Kyverno",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/kyverno/icon/color/kyverno-icon-color.svg",
    category: "Policy Management"
  },
  {
    name: "Knative",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/knative/icon/color/knative-icon-color.svg",
    category: "Serverless"
  },
  {
    name: "Backstage",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/backstage/icon/color/backstage-icon-color.svg",
    category: "Developer Platform"
  },
  {
    name: "Spiffe",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/spiffe/icon/color/spiffe-icon-color.svg",
    category: "Security"
  },
  {
    name: "Spire",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/spire/icon/color/spire-icon-color.svg",
    category: "Security"
  },
  {
    name: "Keda",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/keda/icon/color/keda-icon-color.svg",
    category: "Autoscaling"
  },
  {
    name: "ChaosMesh",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/chaosmesh/icon/color/chaosmesh-icon-color.svg",
    category: "Chaos Engineering"
  },
  {
    name: "Dapr",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/dapr/icon/color/dapr-icon-color.svg",
    category: "Application Runtime"
  },
  {
    name: "CloudEvents",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/cloudevents/icon/color/cloudevents-icon-color.svg",
    category: "Messaging"
  },
  {
    name: "Notary",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/notary/icon/color/notary-icon-color.svg",
    category: "Security"
  },
  {
    name: "Contour",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/contour/icon/color/contour-icon-color.svg",
    category: "Service Proxy"
  },
  {
    name: "Operator Framework",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/operator-framework/icon/color/operator-framework-icon-color.svg",
    category: "Application Management"
  },
  {
    name: "Kubevirt",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/kubevirt/icon/color/kubevirt-icon-color.svg",
    category: "Virtualization"
  },
  {
    name: "Kubeflow",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/kubeflow/icon/color/kubeflow-icon-color.svg",
    category: "Machine Learning"
  },
  {
    name: "Keptn",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/keptn/icon/color/keptn-icon-color.svg",
    category: "Continuous Delivery"
  },
  {
    name: "OpenCost",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/opencost/icon/color/opencost-icon-color.svg",
    category: "Cost Management"
  },
  {
    name: "Cert Manager",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/cert-manager/icon/color/cert-manager-icon-color.svg",
    category: "Security"
  },
  {
    name: "Litmus",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/litmus/icon/color/litmus-icon-color.svg",
    category: "Chaos Engineering"
  },
  {
    name: "Meshery",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/meshery/icon/color/meshery-icon-color.svg",
    category: "Service Mesh Management"
  },
  {
    name: "OpenFeature",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/openfeature/icon/color/openfeature-icon-color.svg",
    category: "Feature Management"
  },
  {
    name: "Pixie",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/pixie/icon/color/pixie-icon-color.svg",
    category: "Observability"
  },
  {
    name: "Armada",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/armada/icon/color/armada-icon-color.svg",
    category: "Job Queuing"
  },
  {
    name: "Carvel",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/carvel/icon/color/carvel-icon-color.svg",
    category: "Package Management"
  },
  {
    name: "Confidential Containers",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/confidential-containers/icon/color/confidential-containers-icon-color.svg",
    category: "Security"
  },
  {
    name: "Curiefense",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/curiefense/icon/color/curiefense-icon-color.svg",
    category: "Security"
  },
  {
    name: "Devfile",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/devfile/icon/color/devfile-icon-color.svg",
    category: "Development"
  },
  {
    name: "Emissary Ingress",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/emissary-ingress/icon/color/emissary-ingress-icon-color.svg",
    category: "API Gateway"
  },
  {
    name: "External Secrets",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/external-secrets/icon/color/external-secrets-icon-color.svg",
    category: "Security"
  },
  {
    name: "Grafana",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/grafana/icon/color/grafana-icon-color.svg",
    category: "Observability"
  },
  {
    name: "in-toto",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/in-toto/icon/color/in-toto-icon-color.svg",
    category: "Security"
  },
  {
    name: "K8GB",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/k8gb/icon/color/k8gb-icon-color.svg",
    category: "Multi-cluster"
  },
  {
    name: "Keylime",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/keylime/icon/color/keylime-icon-color.svg",
    category: "Security"
  },
  {
    name: "Konveyor",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/konveyor/icon/color/konveyor-icon-color.svg",
    category: "Application Migration"
  },
  {
    name: "KubeVela",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/kubevela/icon/color/kubevela-icon-color.svg",
    category: "Application Delivery"
  },
  {
    name: "Kube-OVN",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/kube-ovn/icon/color/kube-ovn-icon-color.svg",
    category: "Networking"
  },
  {
    name: "Kubescape",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/kubescape/icon/color/kubescape-icon-color.svg",
    category: "Security"
  },
  {
    name: "OpenFunction",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/openfunction/icon/color/openfunction-icon-color.svg",
    category: "Serverless"
  },
  {
    name: "OpenKruise",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/openkruise/icon/color/openkruise-icon-color.svg",
    category: "Application Management"
  },
  {
    name: "Pravega",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/pravega/icon/color/pravega-icon-color.svg",
    category: "Storage"
  },
  {
    name: "SchemaHero",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/schemahero/icon/color/schemahero-icon-color.svg",
    category: "Database Management"
  },
  {
    name: "Submariner",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/submariner/icon/color/submariner-icon-color.svg",
    category: "Multi-cluster Networking"
  },
  {
    name: "Teller",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/teller/icon/color/teller-icon-color.svg",
    category: "Secret Management"
  },
  {
    name: "WasmEdge",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/wasmedge/icon/color/wasmedge-icon-color.svg",
    category: "Runtime"
  },
  {
    name: "Antrea",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/antrea/icon/color/antrea-icon-color.svg",
    category: "Networking"
  },
  {
    name: "Clusterpedia",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/clusterpedia/icon/color/clusterpedia-icon-color.svg",
    category: "Multi-cluster"
  },
  {
    name: "Karmada",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/karmada/icon/color/karmada-icon-color.svg",
    category: "Multi-cluster Management"
  },
  {
    name: "KubeArmor",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/kubearmor/icon/color/kubearmor-icon-color.svg",
    category: "Security"
  },
  {
    name: "OpenELB",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/openelb/icon/color/openelb-icon-color.svg",
    category: "Load Balancing"
  },
  {
    name: "OpenYurt",
    logo: "https://raw.githubusercontent.com/cncf/artwork/master/projects/openyurt/icon/color/openyurt-icon-color.svg",
    category: "Edge Computing"
  }
]; 